import React, { useState, useEffect } from "react";
import { CAPITAL_TYPE_OPTIONS, CAPITAL_TYPE } from "../../constants/capitalTypes";
import styles from "./CapitalFormModal.module.css";

const EMPTY_FORM = {
  type: CAPITAL_TYPE.CASH,
  title: "",
  amount: "",
  date: "",
  notes: "",
};

const CapitalFormModal = ({ isOpen, onClose, onSubmit, initialData }) => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (initialData) {
      setForm({
        type: initialData.type,
        title: initialData.title || "",
        amount: initialData.amount ?? "",
        date: initialData.date ? initialData.date.slice(0, 10) : "",
        notes: initialData.notes || "",
      });
    } else {
      setForm(EMPTY_FORM);
    }
    setErrors({});
  }, [initialData, isOpen]);

  if (!isOpen) return null;

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const validate = () => {
    const newErrors = {};
    if (!form.title.trim()) newErrors.title = "العنوان مطلوب";
    if (!form.amount || Number(form.amount) <= 0) newErrors.amount = "المبلغ يجب أن يكون أكبر من صفر";
    if (!form.date) newErrors.date = "التاريخ مطلوب";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setSubmitting(true);
    try {
      await onSubmit(
        {
          type: Number(form.type),
          title: form.title.trim(),
          amount: Number(form.amount),
          date: new Date(form.date).toISOString(),
          notes: form.notes.trim(),
        },
        setErrors
      );
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()} dir="rtl">
        <div className={styles.header}>
          <h3>{initialData ? "تعديل عنصر" : "إضافة رأس مال جديد"}</h3>
          <button className={styles.closeBtn} onClick={onClose} type="button" aria-label="إغلاق">
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} className={styles.form}>
          <div className={styles.field}>
            <label>النوع</label>
            <select value={form.type} onChange={(e) => handleChange("type", e.target.value)}>
              {CAPITAL_TYPE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          <div className={styles.field}>
            <label>العنوان</label>
            <input
              type="text"
              value={form.title}
              onChange={(e) => handleChange("title", e.target.value)}
              placeholder="مثال: إيداع نقدي"
            />
            {errors.title && <span className={styles.error}>{errors.title}</span>}
          </div>

          <div className={styles.field}>
            <label>المبلغ</label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={form.amount}
              onChange={(e) => handleChange("amount", e.target.value)}
              placeholder="0.00"
            />
            {errors.amount && <span className={styles.error}>{errors.amount}</span>}
          </div>

          <div className={styles.field}>
            <label>التاريخ</label>
            <input
              type="date"
              value={form.date}
              onChange={(e) => handleChange("date", e.target.value)}
            />
            {errors.date && <span className={styles.error}>{errors.date}</span>}
          </div>

          <div className={styles.field}>
            <label>ملاحظات</label>
            <textarea
              rows={3}
              value={form.notes}
              onChange={(e) => handleChange("notes", e.target.value)}
              placeholder="ملاحظات إضافية (اختياري)"
            />
          </div>

          <div className={styles.actions}>
            <button type="button" className={styles.cancelBtn} onClick={onClose}>إلغاء</button>
            <button type="submit" className={styles.submitBtn} disabled={submitting}>
              {submitting ? "جارِ الحفظ..." : "حفظ"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CapitalFormModal;